import moment from 'moment';
import { outreach } from '@round/api';
import { FormValues } from './types';

const DISPLAY_DATE_FORMAT = 'D MMM YYYY';

/** What the endpoint takes a deadline as. */
const DEADLINE_FORMAT = 'YYYY-MM-DD';

/**
 * The window a deadline is offered in, counted from today. The endpoint itself only enforces the
 * data-only minimum so far — the rate minimum is a frontend-only clamp, stricter than what the API
 * currently accepts, until the backend grows the same rate-aware check.
 */
const DATA_ONLY_MIN_DEADLINE_DAYS = 3;
const RATE_MIN_DEADLINE_DAYS = 14;
const MAX_DEADLINE_MONTHS = 3;

/** The endpoint takes a fraction, and rejects anything finer than six decimal places. */
const ENGAGEMENT_RATE_DECIMAL_PLACES = 6;

export const initialValues: FormValues = {
    name: '',
    audios: [],
    hashtags: [],
    outputEmail: false,
    outputLocation: false,
    outputRate: false,
    locations: [],
    locationMatch: 'strict',
    minFollowers: null,
    maxFollowers: null,
    minEngagementRate: null,
    minAvgViews: null,
    deadline: null,
};

/** What an unnamed request is called: the day it was created, e.g. `11 Aug 2026`. */
export const getDefaultRequestName = (today: Date = new Date()) => moment(today).format(DISPLAY_DATE_FORMAT);

export const getRequestName = (name: string, today?: Date) => name.trim() || getDefaultRequestName(today);

export const toEngagementRateFraction = (percentage: number | null) => {
    if (percentage === null) {
        return null;
    }

    return Number((percentage / 100).toFixed(ENGAGEMENT_RATE_DECIMAL_PLACES));
};

export const hasTarget = (values: FormValues) => !!values.audios.length || !!values.hashtags.length;

/**
 * The days a deadline can be picked on. The picker offers nothing outside them, so a date the
 * endpoint would reject is never reachable rather than being answered with a 400 — and, for the
 * rate minimum, a date collection would not have time to run against is never reachable either,
 * even though the endpoint does not yet enforce that half itself.
 */
export const getDeadlineRange = (outputRate: boolean, today: Date = new Date()) => ({
    earliest: moment(today)
        .startOf('day')
        .add(outputRate ? RATE_MIN_DEADLINE_DAYS : DATA_ONLY_MIN_DEADLINE_DAYS, 'days')
        .toDate(),
    latest: moment(today).startOf('day').add(MAX_DEADLINE_MONTHS, 'months').toDate(),
});

export const toDeadlineValue = (date: Date) => moment(date).format(DEADLINE_FORMAT);

export const fromDeadlineValue = (deadline: string | null) =>
    deadline ? moment(deadline, DEADLINE_FORMAT).toDate() : undefined;

export const formatDeadline = (deadline: string) => moment(deadline, DEADLINE_FORMAT).format(DISPLAY_DATE_FORMAT);

/** Tags travel bare, so what is typed as `#Dance` is sent as `Dance`. */
export const normaliseHashtag = (raw: string) => raw.trim().replace(/^#/, '');

/** A tag is one word: nothing left after normalising, or whitespace inside it, is not one. */
export const isValidHashtag = (hashtag: string) => !!hashtag && !/\s/.test(hashtag);

export const mapToApi = (values: FormValues, clientId: number): outreach.PostOutreachRequestData => ({
    client_id: clientId,
    name: getRequestName(values.name),
    targets: {
        audios: values.audios,
        hashtags: values.hashtags,
    },
    outputs: {
        email: values.outputEmail,
        location: values.outputLocation,
        rate: values.outputRate,
    },
    filters: {
        locations: values.locations,
        // The match only means anything against a territory, so with none picked it is sent settled
        // however the toggle was left before the countries came off.
        location_match: values.locations.length ? values.locationMatch : 'strict',
        min_followers: values.minFollowers,
        max_followers: values.maxFollowers,
        min_engagement_rate: toEngagementRateFraction(values.minEngagementRate),
        min_avg_views: values.minAvgViews,
    },
    // Left out rather than sent null, which is what leaves the backend to default it to +3 months.
    ...(values.deadline ? { deadline: values.deadline } : {}),
});
